/**
 * LanguageSelector — UI language switcher for the Training Portal.
 *
 * Options are loaded dynamically from the backend (`GET /api/v1/i18n/locales`)
 * so runtime-installed languages appear automatically. The five default
 * languages (en, zh-CN, zh-TW, th, de) are bundled for instant/offline load;
 * any other installed locale is fetched on demand and registered with i18next
 * via addResourceBundle before switching.
 *
 * Persists selection to localStorage key `ui_language`.
 */

import { useCallback, useEffect, useState } from 'react';
import i18next from 'i18next';
import styles from './LanguageSelector.module.css';

interface LangOption {
  code: string;
  label: string;
}

// Offline fallback if the locales API is unreachable (pre-login / network error).
const FALLBACK_LANGUAGES: LangOption[] = [
  { code: 'en',    label: 'English'  },
  { code: 'zh-CN', label: '简体中文' },
  { code: 'zh-TW', label: '繁體中文' },
  { code: 'th',    label: 'ภาษาไทย'  },
  { code: 'de',    label: 'Deutsch'  },
];

const LS_KEY = 'ui_language';
const NS = 'training';

/** Ensure the pack for `lang` is registered with i18next, fetching it if needed. */
async function ensurePackLoaded(lang: string): Promise<void> {
  if (i18next.hasResourceBundle(lang, NS)) return;
  try {
    const res = await fetch(`/api/v1/i18n/${NS}/${encodeURIComponent(lang)}.json`);
    if (!res.ok) return;
    const pack = await res.json();
    if (pack && Object.keys(pack).length > 0) {
      i18next.addResourceBundle(lang, NS, pack, true, true);
    }
  } catch {
    // Network error — i18next falls back to `fallbackLng` (en).
  }
}

export function LanguageSelector(): JSX.Element {
  const [languages, setLanguages] = useState<LangOption[]>(FALLBACK_LANGUAGES);
  const [currentLang, setCurrentLang] = useState<string>(
    i18next.language ?? localStorage.getItem(LS_KEY) ?? 'en',
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch('/api/v1/i18n/locales');
        if (!res.ok) return;
        const data = await res.json();
        const opts: LangOption[] = (data.locales ?? []).map(
          (l: { code: string; native_name: string }) => ({
            code: l.code,
            label: l.native_name || l.code,
          }),
        );
        if (!cancelled && opts.length > 0) setLanguages(opts);
      } catch {
        // keep fallback list
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleChange = useCallback(
    async (e: React.ChangeEvent<HTMLSelectElement>) => {
      const lang = e.target.value;
      await ensurePackLoaded(lang);
      await i18next.changeLanguage(lang);
      localStorage.setItem(LS_KEY, lang);
      setCurrentLang(lang);
    },
    [],
  );

  return (
    <div className={styles.wrapper} aria-label="Select interface language">
      <span className={styles.globe} aria-hidden="true">🌐</span>
      <select
        className={styles.select}
        value={currentLang}
        onChange={(e) => void handleChange(e)}
        aria-label="Interface language"
      >
        {languages.map(({ code, label }) => (
          <option key={code} value={code}>
            {label}
          </option>
        ))}
      </select>
    </div>
  );
}
